import React, { useState } from 'react';
import './DetailedView.css';
import PrintDetailView from './detailViews/PrintDetailView';
import OnlineDetailView from './detailViews/OnlineDetailView';
import AudioDetailView from './detailViews/AudioDetailView';
import DigitalDetailView from './detailViews/DigitalDetailView';
import { seasonalDates } from '../data/seasonaldates';
import { useLanguage } from '../context/LanguageContext';

// Function to properly parse Dutch and dot-decimal formatted currency
const parseDutchCurrency = (currencyStr) => {
  if (!currencyStr) return 0;
  const s = currencyStr.toString().replace(/€/g, '').trim();
  // If it has a comma, treat as Dutch decimal: 1.234,56 or 12,5
  if (s.includes(',')) {
    return parseFloat(s.replace(/\./g, '').replace(/,/g, '.')) || 0;
  }
  // If no comma and looks like a dot-decimal number (e.g., 12.5), parse directly
  const dotCount = (s.match(/\./g) || []).length;
  if (dotCount === 1 && /^\d+\.\d+$/.test(s)) {
    return parseFloat(s) || 0;
  }
  // Otherwise treat dots as thousands and strip them
  return parseFloat(s.replace(/\./g, '')) || 0;
};

// Format price with comma as decimal separator and dot as thousands separator
const formatPrice = (price) => {
  if (price === null || isNaN(price)) return '0,00';
  
  // Format with 2 decimal places and replace decimal point with comma
  let formattedPrice = price.toFixed(2).replace('.', ',');
  
  // Add thousands separator (dot)
  if (price >= 1000) {
    const parts = formattedPrice.split(',');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    formattedPrice = parts.join(',');
  }
  
  return formattedPrice;
};

// Calculate the total price (Basisprijs x Positiefactor x Formaatfactor)
const calculateTotalPrice = (row) => {
  if (!row['Basisprijs']) return row['Basisprijs'] || '-';
  
  // Only calculate for Print medium
  if (row.Medium !== 'Print') return row['Basisprijs'];
  
  // Parse the base price from Dutch currency format
  const basePrice = parseDutchCurrency(row['Basisprijs']);
  if (isNaN(basePrice)) return row['Basisprijs'];
  
  // Get the position factor and format factor, default to 1 if not present
  const positionFactor = parseDutchCurrency(row['Positiefactor']) || 1;
  const formatFactor = parseDutchCurrency(row['Formaatfactor']) || 1;
  
  // Calculate the total price
  const totalPrice = basePrice * positionFactor * formatFactor;
  
  // Format and return the price
  return `€ ${formatPrice(totalPrice)}`;
};

// Main DetailedView component that renders the appropriate view based on medium type
const DetailedView = ({ selectedRow, columns }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const { t } = useLanguage();

  if (!selectedRow) {
    return (
      <div className="detailed-view empty-view">
        <p>{t('selectRowToViewDetails', 'Select a row to view details')}</p>
      </div>
    );
  }
  
  // Format seasonal dates for the tooltip
  const formatSeasonalDatesInfo = () => {
    return (
      <div className="seasonal-dates-tooltip">
        <h4>Seizoen Toeslagen</h4>
        <ul>
          {seasonalDates.map(period => (
            <li key={period.id}>
              <strong>{period.name}:</strong> {period.days.length} dagen
              <br/>
              <span className="date-range">{period.days[0]} t/m {period.days[period.days.length - 1]}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  };
  
  // Determine which detail view to render based on medium type
  const mediumType = selectedRow['Mediumtype'];
  const medium = selectedRow['Medium'];
  
  if (medium === 'Print') {
    return <PrintDetailView selectedRow={selectedRow} columns={columns} />;
  } else if (mediumType === 'Nieuwsbrief') {
    return <DigitalDetailView selectedRow={selectedRow} columns={columns} />;
  } else if (mediumType === 'Online' || mediumType === 'Display' || mediumType === 'Video') {
    return <OnlineDetailView selectedRow={selectedRow} columns={columns} />;
  } else if (mediumType === 'Radio' || mediumType === 'Podcast') {
    return <AudioDetailView selectedRow={selectedRow} columns={columns} />;
  } else if (mediumType === 'Social') {
    // Social rows keep their original Medium (Video/Display/Podcast); route to the matching view
    if (medium === 'Podcast') {
      return <AudioDetailView selectedRow={selectedRow} columns={columns} />;
    }
    return <OnlineDetailView selectedRow={selectedRow} columns={columns} />;
  } else {
    // Fallback to a generic view for other medium types
    return (
      <div className="detailed-view">
        <h3 className="detailed-view-title">
          {mediumType === 'Nieuwsblad' || mediumType === 'Weekblad'
            ? 'Nieuws- en weekbladen Details'
            : (mediumType && mediumType.startsWith('Dagblad'))
              ? 'Dagblad Details'
              : `${mediumType} Details`}
        </h3>
        
        <div className="detail-section">
          <strong className="detailed-field-label">Title</strong>
          <div className="detailed-field-value">{selectedRow['Titel'] || '-'}</div>
        </div>
        
        <div className="detail-section">
          <strong className="detailed-field-label">Format</strong>
          <div className="detailed-field-value">{selectedRow['Formaat'] || '-'}</div>
        </div>
        
        <div className="detail-section">
          <strong className="detailed-field-label">Totaal prijs</strong>
          <div className="detailed-field-value price">
            {medium === 'Print' ? calculateTotalPrice(selectedRow) : `€ ${selectedRow['Basisprijs'] || '-'}`}
          </div>
          {selectedRow['Opmerkingen'] && (
            <div className="detailed-field-notes">{selectedRow['Opmerkingen']}</div>
          )}
          
          {medium === 'Print' && (
            <div className="seasonal-surcharge-info">
              <span className="info-text">Let op, prijzen zijn exclusief seizoens toeslag</span>
              <div className="icon-surcharge-info" 
                   onMouseEnter={() => setShowTooltip(true)}
                   onMouseLeave={() => setShowTooltip(false)}>
                {showTooltip && (
                  <div className="seasonal-tooltip">
                    {formatSeasonalDatesInfo()}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        
        <div className="detail-section">
          <strong className="detailed-field-label">Additional Information</strong>
          {columns
            .filter(column => !['Mediumtype', 'Titel', 'Formaat', 'Opmerkingen'].includes(column))
            .map((column, index) => (
              <div key={index} className="detail-item">
                <span className="detail-item-label">{column}:</span>
                <span className="detail-item-value">{selectedRow[column] || '-'}</span>
              </div>
            ))}
        </div>
      </div>
    );
  }
};

export default DetailedView; 